import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  CalendarClock,
  CalendarDays,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Trash2,
  CircleUserRound,
  ShieldUser,
  LayoutGrid,
  Table,
} from "lucide-react";
import Topbar from "../components/Topbar";
import FilterBar from "../components/controls/FilterBar";
import SearchInput from "../components/controls/SearchInput";
import SelectField from "../components/controls/SelectField";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import AdminUserModal from "../components/users/AdminUserModal";
import { listAdminUsers, deleteAdminUser } from "../api/users";
import { listCompanies } from "../api/companies";
import { AdminUserRow, UserRole } from "../types/users";
import { SCompanyRow } from "../types/companies";
import { useAuth } from "../context/AuthContext";
import { useDebouncedUrlQuery } from "../hooks/useDebouncedUrlQuery";

type SortField = "id" | "fullname" | "email" | "last_seen_at" | "subscription_expires_on";
type SortDirection = "asc" | "desc";

const SORT_LABELS: Record<SortField, string> = {
  id: "ID",
  fullname: "ФИО",
  email: "Email",
  last_seen_at: "Дата входа",
  subscription_expires_on: "Подписка",
};

const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Администратор",
  client: "Клиент",
};
const MULTI_ACCOUNT_LABELS = {
  full: "Полный",
  partial: "Частичный",
} as const;

function formatMultiAccount(user: AdminUserRow) {
  if (!user.multi_account_type) return "—";
  if (user.multi_account_type === "partial") {
    const count = user.accessible_company_ids?.length ?? user.accessible_companies?.length ?? 0;
    return count ? `${MULTI_ACCOUNT_LABELS.partial} (${count})` : MULTI_ACCOUNT_LABELS.partial;
  }
  return MULTI_ACCOUNT_LABELS[user.multi_account_type];
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ru-RU");
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [viewMode, setViewMode] = useState<"cards" | "table">(() => {
    if (typeof window === "undefined") return "cards";
    const saved = window.localStorage.getItem("admin-users:view");
    return saved === "table" ? "table" : "cards";
  });
  const [items, setItems] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [companies, setCompanies] = useState<SCompanyRow[]>([]);

  const [roleDraft, setRoleDraft] = useState<UserRole | "">("");
  const [companyDraft, setCompanyDraft] = useState<string>("");
  const [hideExpiredDraft, setHideExpiredDraft] = useState(false);
  const [roleFilter, setRoleFilter] = useState<UserRole | "">("");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [hideExpiredFilter, setHideExpiredFilter] = useState(false);

  const [orderBy, setOrderBy] = useState<SortField>("id");
  const [orderDir, setOrderDir] = useState<SortDirection>("desc");
  const [searchDraft, setSearchDraft, searchQuery] = useDebouncedUrlQuery();
  const requestIdRef = useRef(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingUser, setEditingUser] = useState<AdminUserRow | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<AdminUserRow | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await listCompanies({ page: 1, per_page: 200 });
        setCompanies(Array.isArray(response.items) ? response.items : []);
      } catch (e) {
        console.error("Не удалось загрузить список компаний", e);
      }
    })();
  }, []);

  const fetchUsers = useCallback(
    async (targetPage: number) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError("");
      try {
        const response = await listAdminUsers({
          page: targetPage,
          per_page: perPage,
          q: searchQuery || null,
          role: roleFilter ? (roleFilter as UserRole) : null,
          company_id: companyFilter ? Number(companyFilter) : null,
          order_by: orderBy,
          order_dir: orderDir,
          hide_expired_subscription: hideExpiredFilter,
        });
        if (requestId !== requestIdRef.current) return;
        setItems(Array.isArray(response.items) ? response.items : []);
        setTotal(typeof response.total === "number" ? response.total : 0);
      } catch (e: any) {
        if (requestId !== requestIdRef.current) return;
        setError(e?.response?.data?.detail || "Ошибка загрузки пользователей");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [companyFilter, hideExpiredFilter, orderBy, orderDir, perPage, roleFilter, searchQuery],
  );

  useEffect(() => {
    fetchUsers(page);
  }, [fetchUsers, page]);

  useEffect(() => {
    window.localStorage.setItem("admin-users:view", viewMode);
  }, [viewMode]);

  const companyOptions = useMemo(
    () =>
      companies
        .filter((company) => !company.deleted_at)
        .sort((a, b) => a.company_name.localeCompare(b.company_name))
        .map((company) => ({
          label: company.company_name,
          value: String(company.id),
        })),
    [companies],
  );

  const roleOptions = useMemo(
    () =>
      (["client", "admin"] as UserRole[]).map((value) => ({
        label: ROLE_LABELS[value],
        value,
      })),
    [],
  );

  const pages = Math.max(1, Math.ceil(total / perPage));
  const hasActiveFilters = Boolean(roleFilter || companyFilter || hideExpiredFilter);

  function applyFilters() {
    setRoleFilter(roleDraft);
    setCompanyFilter(companyDraft);
    setHideExpiredFilter(hideExpiredDraft);
    setPage(1);
  }

  function resetFilters() {
    setRoleDraft("");
    setCompanyDraft("");
    setHideExpiredDraft(false);
    setRoleFilter("");
    setCompanyFilter("");
    setHideExpiredFilter(false);
    setPage(1);
  }

  function toggleSort(field: SortField) {
    setPage(1);
    if (orderBy === field) {
      setOrderDir(orderDir === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(field);
      setOrderDir("asc");
    }
  }

  function renderSortIcon(field: SortField) {
    if (orderBy !== field) {
      return <ChevronsUpDown size={16} className="text-slate-400" />;
    }
    return orderDir === "asc" ? (
      <ChevronUp size={16} className="text-white" />
    ) : (
      <ChevronDown size={16} className="text-white" />
    );
  }

  async function handleDeleteConfirm() {
    if (!deletingUser) {
      throw new Error("Пользователь не выбран");
    }
    const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
    await deleteAdminUser(deletingUser.id);
    if (nextPage === page) {
      await fetchUsers(page);
    } else {
      setPage(nextPage);
    }
  }

  function handleModalSuccess(user: AdminUserRow, mode: "create" | "edit") {
    if (mode === "create") {
      if (page !== 1) {
        setPage(1);
      } else {
        fetchUsers(1);
      }
    } else {
      fetchUsers(page);
      setEditingUser(null);
    }
  }

  const stats = useMemo(() => {
    const admins = items.filter(u => u.role === "admin").length;
    const clients = items.filter(u => u.role === "client").length;
    return [
      { label: "Всего пользователей", value: total.toLocaleString("ru-RU"), desc: "в системе TravelStat" },
      { label: "Администраторы", value: admins.toString(), desc: "управляют компаниями и доступами" },
      { label: "Клиенты", value: clients.toString(), desc: "работают с объектами и аналитикой" },
    ];
  }, [items, total]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[1200px] px-6 py-10 space-y-8">
        <section className="rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Пользователи</h1>
              <p className="mt-2 text-sm text-slate-500">Управляйте доступами администраторов и клиентов, назначайте компании и роли</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setModalMode("create");
                setEditingUser(null);
                setModalOpen(true);
              }}
              className="inline-flex items-center gap-2 self-start rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(99,102,241,0.25)] transition hover:translate-y-[-1px] hover:brightness-105"
            >
              <span className="text-lg leading-none">+</span> Добавить пользователя
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-3">
          {stats.map((stat, idx) => (
            <div key={idx} className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
              <p className="text-xs uppercase tracking-wide text-slate-400">{stat.label}</p>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{stat.value}</div>
              <p className="mt-1 text-sm text-slate-500">{stat.desc}</p>
            </div>
          ))}
        </section>

        {error && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
            {error}
          </div>
        )}

        <AdminUserModal
          open={modalOpen}
          mode={modalMode}
          initialUser={modalMode === "edit" ? editingUser ?? undefined : undefined}
          companies={companies}
          onClose={() => {
            setModalOpen(false);
            if (modalMode === "edit") {
              setEditingUser(null);
            }
          }}
          onSuccess={handleModalSuccess}
        />

        <ConfirmDeleteModal
          open={deleteOpen}
          onClose={() => {
            setDeleteOpen(false);
            setDeletingUser(null);
          }}
          onConfirm={async () => {
            await handleDeleteConfirm();
            setDeleteOpen(false);
            setDeletingUser(null);
          }}
          title="Удаление пользователя"
          message="Вы уверены что хотите удалить пользователя?"
        />

        <FilterBar
          onApply={applyFilters}
          onReset={resetFilters}
          hasActive={hasActiveFilters}
          applyDisabled={loading}
          className="mb-6"
          sticky={false}
        >
          <SelectField
            value={roleDraft}
            onChange={(value) => setRoleDraft(value as UserRole | "")}
            options={roleOptions}
            placeholder="Роль"
            icon={<CheckCircle2 size={18} />}
          />
          <SelectField
            value={companyDraft}
            onChange={(value) => setCompanyDraft(String(value))}
            options={companyOptions}
            placeholder="Компания"
            icon={<Building2 size={18} />}
          />
          <label className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
            <input
              type="checkbox"
              checked={hideExpiredDraft}
              onChange={(event) => setHideExpiredDraft(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Скрыть с истекшей подпиской
          </label>
        </FilterBar>

        <section className="grid gap-3 rounded-3xl border border-indigo-100 bg-gradient-to-r from-white via-indigo-50/70 to-white p-4 shadow-[0_12px_30px_rgba(99,102,241,0.08)] md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Поиск пользователей</p>
            <SearchInput
              value={searchDraft}
              onChange={(value) => {
                setSearchDraft(value);
                setPage(1);
              }}
              placeholder="Поиск по ФИО или почте"
              variant="soft"
              className="mt-2"
            />
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm text-slate-500 shadow-sm md:text-right">
            <div className="text-xs uppercase tracking-wide text-slate-400">В выдаче</div>
            <div className="mt-1 text-xl font-semibold text-slate-900">{total.toLocaleString("ru-RU")}</div>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-white/70 bg-white/90 p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-500">Сортировка:</span>
            {(Object.keys(SORT_LABELS) as SortField[]).map((field) => {
              const isActive = orderBy === field;
              return (
                <button
                  key={field}
                  type="button"
                  onClick={() => toggleSort(field)}
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition",
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100",
                  ].join(" ")}
                >
                  {SORT_LABELS[field]}
                  <span className={isActive ? "text-white" : ""}>{renderSortIcon(field)}</span>
                </button>
              );
            })}
          </div>
          <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                viewMode === "cards" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <LayoutGrid size={16} />
              Плашки
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                viewMode === "table" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Table size={16} />
              Таблица
            </button>
          </div>
        </div>

        {loading && (
          <div className="rounded-3xl border border-slate-100 bg-white px-6 py-12 text-center text-slate-500 shadow-[0_6px_24px_rgba(0,0,0,.06)]">
            <div className="inline-flex items-center gap-2 text-sm">
              <Loader2 className="animate-spin" size={18} />
              Загрузка пользователей…
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="rounded-3xl border border-slate-100 bg-white px-6 py-12 text-center text-slate-500 shadow-[0_6px_24px_rgba(0,0,0,.06)]">
            {searchQuery ? "Ничего не найдено" : "Пользователи не найдены"}
          </div>
        )}

        {!loading && items.length > 0 && viewMode === "cards" && (
          <div className="space-y-4">
            {items.map((item) => {
              const isCurrent = !!currentUser && currentUser.id === item.id;
              return (
                <div
                  key={item.id}
                  className="rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_6px_24px_rgba(0,0,0,.06)] transition hover:shadow-lg"
                >
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1.25fr)_minmax(0,1.25fr)_auto] md:items-center">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Пользователь</div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-[1.05rem] font-semibold text-slate-900">
                          {item.fullname || "—"}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">
                          ID: {item.id}
                        </span>
                      </div>
                      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-sm text-blue-700">
                        <Mail size={16} />
                        <span className="max-w-[220px] truncate">{item.email}</span>
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-sm text-slate-600">
                        <Phone size={16} />
                        <span className="max-w-[220px] truncate">{item.phone || "—"}</span>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Роль</div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
                        {item.role === "admin" ? (
                          <ShieldUser size={16} className="text-emerald-600" />
                        ) : (
                          <CircleUserRound size={16} className="text-slate-500" />
                        )}
                        <span>{ROLE_LABELS[item.role] || item.role}</span>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Компания</div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
                        <Building2 size={16} className="text-slate-500" />
                        {item.company_name || "—"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Мультиаккаунт</div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={16} className="text-slate-500" />
                        {formatMultiAccount(item)}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Дата входа</div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
                        <CalendarClock size={16} className="text-slate-500" />
                        {formatDateTime(item.last_seen_at)}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Подписка до</div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
                        <CalendarDays size={16} className="text-slate-500" />
                        {formatDate(item.subscription_expires_on)}
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setModalMode("edit");
                          setEditingUser(item);
                          setModalOpen(true);
                        }}
                        className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeletingUser(item);
                          setDeleteOpen(true);
                        }}
                        disabled={isCurrent}
                        className="rounded-xl border border-slate-200 bg-white p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && items.length > 0 && viewMode === "table" && (
          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_6px_24px_rgba(0,0,0,.06)]">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-5 py-4">Пользователь</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Телефон</th>
                    <th className="px-5 py-4">Роль</th>
                    <th className="px-5 py-4">Компания</th>
                    <th className="px-5 py-4">Мультиаккаунт</th>
                    <th className="px-5 py-4">Дата входа</th>
                    <th className="px-5 py-4">Подписка до</th>
                    <th className="px-5 py-4 text-right">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => {
                    const isCurrent = !!currentUser && currentUser.id === item.id;
                    return (
                      <tr key={item.id} className="transition hover:bg-slate-50/70">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-900">{item.fullname || "—"}</div>
                          <div className="text-xs uppercase tracking-wide text-slate-400">ID {item.id}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <Mail size={16} className="text-slate-500" />
                            <span className="max-w-[240px] truncate">{item.email}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <Phone size={16} className="text-slate-500" />
                            <span className="max-w-[180px] truncate">{item.phone || "—"}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            {item.role === "admin" ? (
                              <ShieldUser size={16} className="text-emerald-600" />
                            ) : (
                              <CircleUserRound size={16} className="text-slate-500" />
                            )}
                            <span>{ROLE_LABELS[item.role] || item.role}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <Building2 size={16} className="text-slate-500" />
                            {item.company_name || "—"}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <CheckCircle2 size={16} className="text-slate-500" />
                            {formatMultiAccount(item)}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <CalendarClock size={16} className="text-slate-500" />
                            {formatDateTime(item.last_seen_at)}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <CalendarDays size={16} className="text-slate-500" />
                            {formatDate(item.subscription_expires_on)}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setModalMode("edit");
                                setEditingUser(item);
                                setModalOpen(true);
                              }}
                              className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setDeletingUser(item);
                                setDeleteOpen(true);
                              }}
                              disabled={isCurrent}
                              className="rounded-xl border border-slate-200 bg-white p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:bg-slate-50 disabled:text-slate-400"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <section className="flex flex-wrap items-center gap-4 rounded-3xl border border-white/70 bg-white/80 p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600" htmlFor="admin-users-per-page">
              На странице:
            </label>
            <select
              id="admin-users-per-page"
              value={perPage}
              onChange={(event) => {
                const next = Number(event.target.value);
                setPerPage(next);
                setPage(1);
              }}
              className="rounded-2xl border bg-white px-3 py-2"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 mx-auto">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Назад
            </button>
            <div className="rounded-2xl bg-slate-100 px-3 py-2 text-slate-800">{page} / {pages}</div>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(pages, prev + 1))}
              disabled={page === pages}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Вперёд
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
